import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TrainerService } from '../../../../@shared/services/trainer.service';
import { TrainerStatusResponse, TrainerImageResponse, TrainerApplicationResponse } from '../../../../@shared/models/response.module';
import { TRN_4, TRN_13, TRN_16 } from '../../../../@shared/models/input.module';
import { StorageService } from '../../../../@core/services/storage.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../../@core/services/toast.service';

@Component({
  selector: 'app-user-profile-trainer',
  standalone: false,
  templateUrl: './user-profile-trainer.html',
  styleUrl: './user-profile-trainer.css'
})
export class UserProfileTrainer implements OnInit {
  loading: boolean = false;
  saving: boolean = false;
  trainerStatus: TrainerStatusResponse | null = null;

  // Images
  images: TrainerImageResponse[] = [];
  loadingImages: boolean = false;
  uploadingImage: boolean = false;
  uploadProgress: string = '';

  // Form fields (for active trainers)
  trainerDescription: string = '';
  trainerExperience: string = '';
  trainerCertificates: string = '';
  trainerAchievements: string = '';
  trainerSpecializations: string = '';

  // Social media fields
  trainerWebsiteUrl: string = '';
  trainerFacebookUrl: string = '';
  trainerInstagramUrl: string = '';
  trainerYoutubeUrl: string = '';
  trainerTiktokUrl: string = '';

  // Application modal
  showApplicationModal: boolean = false;
  submittingApplication: boolean = false;
  applicationForm: TRN_13 = {
    trainer_description: '',
    trainer_experience: '',
    trainer_certificates: '',
    trainer_achievements: '',
    trainer_specializations: '',
    contact_email: '',
    contact_phone: ''
  };

  // Recommendation modal
  showRecommendModal: boolean = false;
  submittingRecommendation: boolean = false;
  recommendForm: TRN_16 = {
    trainer_name: '',
    description: '',
    specializations: '',
    contact_info: ''
  };

  // Withdraw application
  withdrawing: boolean = false;

  // Predefined specializations - matches event disciplines
  availableSpecializations: string[] = [
    'dressage',
    'jumping',
    'eventing',
    'driving',
    'vaulting',
    'distance',
    'icelandic',
    'western',
    'para_dressage',
    'mounted_games',
    'working_eq',
    'polo',
    'other'
  ];

  constructor(
    private trainerService: TrainerService,
    private route: ActivatedRoute,
    private storageService: StorageService,
    private translateService: TranslateService,
    private toastService: ToastService
  ) {}

  ngOnInit() {
    // Check for success/cancelled query params
    this.route.queryParams.subscribe(params => {
      if (params['success'] === 'true') {
        // Subscription was successful, reload status
        this.loadTrainerStatus();
      }
    });

    // Pre-fill contact info for application form
    const user = this.storageService.getUser();
    if (user) {
      this.applicationForm.contact_email = user.email || '';
      this.applicationForm.contact_phone = user.phone || '';
    }

    this.loadTrainerStatus();
  }

  loadTrainerStatus() {
    this.loading = true;
    this.trainerService.getStatus().subscribe({
      next: (status) => {
        this.trainerStatus = status;
        this.populateForm();
        this.loading = false;
        // Load images if user is an active trainer
        if (status?.is_trainer && status?.trainer_status === 'active') {
          this.loadImages();
        }
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  populateForm() {
    if (this.trainerStatus) {
      this.trainerDescription = this.trainerStatus.trainer_description || '';
      this.trainerExperience = this.trainerStatus.trainer_experience || '';
      this.trainerCertificates = this.trainerStatus.trainer_certificates || '';
      this.trainerAchievements = this.trainerStatus.trainer_achievements || '';
      const rawSpecs = this.trainerStatus.trainer_specializations || '';
      const normalized = rawSpecs.split(',').map(s => this.normalizeSpec(s)).filter(s => s);
      const unique = [...new Set(normalized)];
      this.trainerSpecializations = unique.join(', ');
      this.trainerWebsiteUrl = this.trainerStatus.trainer_website_url || '';
      this.trainerFacebookUrl = this.trainerStatus.trainer_facebook_url || '';
      this.trainerInstagramUrl = this.trainerStatus.trainer_instagram_url || '';
      this.trainerYoutubeUrl = this.trainerStatus.trainer_youtube_url || '';
      this.trainerTiktokUrl = this.trainerStatus.trainer_tiktok_url || '';
    }
  }

  // State checks
  isActiveTrainer(): boolean {
    return this.trainerStatus?.is_trainer === true && this.trainerStatus?.trainer_status === 'active';
  }

  isCancelledTrainer(): boolean {
    return this.trainerStatus?.trainer_status === 'cancelled';
  }

  hasPendingApplication(): boolean {
    return this.trainerStatus?.has_pending_application === true;
  }

  isRejectedApplication(): boolean {
    return this.trainerStatus?.application?.status === 'rejected';
  }

  getApplication(): TrainerApplicationResponse | null {
    return this.trainerStatus?.application || null;
  }

  getExpiresAt(): string {
    if (!this.trainerStatus?.expires_at) return '';
    const date = new Date(this.trainerStatus.expires_at);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  }

  formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  }

  // ==================== APPLICATION METHODS ====================

  openApplicationModal() {
    // Pre-fill contact info
    const user = this.storageService.getUser();
    if (user) {
      this.applicationForm.contact_email = user.email || '';
      this.applicationForm.contact_phone = user.phone || '';
    }
    this.showApplicationModal = true;
  }

  closeApplicationModal() {
    this.showApplicationModal = false;
  }

  submitApplication() {
    // Validate required fields
    if (!this.applicationForm.trainer_description?.trim()) {
      this.toastService.error(this.translateService.instant('trainer.descriptionRequired'));
      return;
    }
    if (!this.applicationForm.trainer_experience?.trim()) {
      this.toastService.error(this.translateService.instant('trainer.experienceRequired'));
      return;
    }

    this.submittingApplication = true;
    this.trainerService.submitApplication(this.applicationForm).subscribe({
      next: () => {
        this.toastService.success(this.translateService.instant('trainer.applicationSubmitted'));
        this.showApplicationModal = false;
        this.submittingApplication = false;
        // Reset form
        this.applicationForm = {
          trainer_description: '',
          trainer_experience: '',
          trainer_certificates: '',
          trainer_achievements: '',
          trainer_specializations: '',
          contact_email: '',
          contact_phone: ''
        };
        this.loadTrainerStatus();
      },
      error: () => {
        this.submittingApplication = false;
      }
    });
  }

  withdrawApplication() {
    if (!confirm(this.translateService.instant('trainer.withdrawConfirm'))) {
      return;
    }

    this.withdrawing = true;
    this.trainerService.withdrawApplication().subscribe({
      next: () => {
        this.toastService.success(this.translateService.instant('trainer.applicationWithdrawn'));
        this.withdrawing = false;
        this.loadTrainerStatus();
      },
      error: () => {
        this.withdrawing = false;
      }
    });
  }

  // ==================== RECOMMENDATION METHODS ====================

  openRecommendModal() {
    this.showRecommendModal = true;
  }

  closeRecommendModal() {
    this.showRecommendModal = false;
  }

  submitRecommendation() {
    // Validate required fields
    if (!this.recommendForm.trainer_name?.trim()) {
      this.toastService.error(this.translateService.instant('trainer.nameRequired'));
      return;
    }
    if (!this.recommendForm.description?.trim()) {
      this.toastService.error(this.translateService.instant('trainer.reasonRequired'));
      return;
    }

    this.submittingRecommendation = true;
    this.trainerService.submitRecommendation(this.recommendForm).subscribe({
      next: () => {
        this.toastService.success(this.translateService.instant('trainer.recommendationSubmitted'));
        this.showRecommendModal = false;
        this.submittingRecommendation = false;
        // Reset form
        this.recommendForm = {
          trainer_name: '',
          description: '',
          specializations: '',
          contact_info: ''
        };
      },
      error: () => {
        this.submittingRecommendation = false;
      }
    });
  }

  // ==================== PROFILE METHODS (for active trainers) ====================

  // Subscribe to become a trainer (legacy - kept for renewal)
  subscribe() {
    this.trainerService.subscribe().subscribe({
      next: (result) => {
        if (result.checkout_url) {
          window.location.href = result.checkout_url;
        }
      },
      error: () => {
        // Handle error
      }
    });
  }

  // Update trainer profile
  saveProfile() {
    this.saving = true;
    const params: TRN_4 = {
      trainer_description: this.trainerDescription,
      trainer_experience: this.trainerExperience,
      trainer_certificates: this.trainerCertificates,
      trainer_achievements: this.trainerAchievements,
      trainer_specializations: this.trainerSpecializations,
      trainer_website_url: this.trainerWebsiteUrl,
      trainer_facebook_url: this.trainerFacebookUrl,
      trainer_instagram_url: this.trainerInstagramUrl,
      trainer_youtube_url: this.trainerYoutubeUrl,
      trainer_tiktok_url: this.trainerTiktokUrl
    };

    this.trainerService.updateProfile(params).subscribe({
      next: () => {
        this.saving = false;
        this.toastService.success(this.translateService.instant('trainer.profileSaved'));
        this.loadTrainerStatus();
      },
      error: () => {
        this.saving = false;
      }
    });
  }

  // Cancel subscription
  cancelSubscription() {
    if (!confirm(this.translateService.instant('trainer.cancelConfirm'))) {
      return;
    }

    this.trainerService.cancelSubscription().subscribe({
      next: () => {
        this.loadTrainerStatus();
      }
    });
  }

  // Helper to toggle specialization (for profile form)
  toggleSpecialization(spec: string) {
    const currentSpecs = this.trainerSpecializations
      .split(',')
      .map(s => s.trim())
      .filter(s => s);

    const index = currentSpecs.indexOf(spec);
    if (index >= 0) {
      currentSpecs.splice(index, 1);
    } else {
      currentSpecs.push(spec);
    }

    this.trainerSpecializations = currentSpecs.join(', ');
  }

  isSpecializationSelected(spec: string): boolean {
    const currentSpecs = this.trainerSpecializations
      .split(',')
      .map(s => s.trim())
      .filter(s => s);
    return currentSpecs.includes(spec);
  }

  // Helper to toggle specialization (for application form)
  toggleApplicationSpecialization(spec: string) {
    const currentSpecs = (this.applicationForm.trainer_specializations || '')
      .split(',')
      .map(s => s.trim())
      .filter(s => s);

    const index = currentSpecs.indexOf(spec);
    if (index >= 0) {
      currentSpecs.splice(index, 1);
    } else {
      currentSpecs.push(spec);
    }

    this.applicationForm.trainer_specializations = currentSpecs.join(', ');
  }

  isApplicationSpecializationSelected(spec: string): boolean {
    const currentSpecs = (this.applicationForm.trainer_specializations || '')
      .split(',')
      .map(s => s.trim())
      .filter(s => s);
    return currentSpecs.includes(spec);
  }

  // Helper to toggle specialization (for recommendation form)
  toggleRecommendSpecialization(spec: string) {
    const currentSpecs = (this.recommendForm.specializations || '')
      .split(',')
      .map(s => s.trim())
      .filter(s => s);

    const index = currentSpecs.indexOf(spec);
    if (index >= 0) {
      currentSpecs.splice(index, 1);
    } else {
      currentSpecs.push(spec);
    }

    this.recommendForm.specializations = currentSpecs.join(', ');
  }

  isRecommendSpecializationSelected(spec: string): boolean {
    const currentSpecs = (this.recommendForm.specializations || '')
      .split(',')
      .map(s => s.trim())
      .filter(s => s);
    return currentSpecs.includes(spec);
  }

  private normalizeSpec(spec: string): string {
    const oldToKey: { [key: string]: string } = {
      'Dressage': 'dressage', 'Jumping': 'jumping', 'Western': 'western',
      'Eventing': 'eventing', 'Reining': 'western', 'Trail Riding': 'distance',
      'Barrel Racing': 'western', 'Pole Bending': 'western',
      'Horsemanship': 'other', 'Ground Work': 'other', 'Liberty Training': 'other'
    };
    const trimmed = spec.trim();
    if (this.availableSpecializations.includes(trimmed)) return trimmed;
    return oldToKey[trimmed] || trimmed;
  }

  translateSpecialization(spec: string): string {
    // Map old English values to discipline keys for backward compatibility
    const oldToKey: { [key: string]: string } = {
      'Dressage': 'dressage', 'Jumping': 'jumping', 'Western': 'western',
      'Eventing': 'eventing', 'Reining': 'western', 'Trail Riding': 'distance',
      'Barrel Racing': 'western', 'Pole Bending': 'western',
      'Horsemanship': 'other', 'Ground Work': 'other', 'Liberty Training': 'other'
    };
    const key = oldToKey[spec] || spec;
    const i18nKey = `trainers.spec.${key}`;
    const translated = this.translateService.instant(i18nKey);
    return translated !== i18nKey ? translated : spec;
  }

  // Image management
  loadImages() {
    const user = this.storageService.getUser();
    if (!user?.user_id) return;

    this.loadingImages = true;
    this.trainerService.getTrainerImages(user.user_id).subscribe({
      next: (images) => {
        this.images = images || [];
        this.loadingImages = false;
      },
      error: () => {
        this.loadingImages = false;
      }
    });
  }

  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const files: File[] = [];
    const errors: string[] = [];

    // Validate all files
    for (let i = 0; i < input.files.length; i++) {
      const file = input.files[i];

      if (!file.type.startsWith('image/')) {
        errors.push(`${file.name}: Not an image file`);
        continue;
      }

      if (file.size > 5 * 1024 * 1024) {
        errors.push(`${file.name}: Exceeds 5MB limit`);
        continue;
      }

      files.push(file);
    }

    if (errors.length > 0) {
      alert('Some files were skipped:\n' + errors.join('\n'));
    }

    if (files.length > 0) {
      this.uploadImages(files);
    }

    input.value = ''; // Reset input
  }

  uploadImages(files: File[]) {
    this.uploadingImage = true;
    let completed = 0;
    const total = files.length;

    this.uploadProgress = `(${completed}/${total})`;

    files.forEach((file) => {
      this.trainerService.uploadImage(file).subscribe({
        next: (image) => {
          this.images.push(image);
          completed++;
          this.uploadProgress = `(${completed}/${total})`;

          if (completed === total) {
            this.uploadingImage = false;
            this.uploadProgress = '';
          }
        },
        error: () => {
          completed++;
          this.uploadProgress = `(${completed}/${total})`;

          if (completed === total) {
            this.uploadingImage = false;
            this.uploadProgress = '';
          }
        }
      });
    });
  }

  deleteImage(image: TrainerImageResponse) {
    if (!confirm(this.translateService.instant('trainer.deleteImageConfirm'))) return;

    this.trainerService.deleteImage(image.image_id).subscribe({
      next: () => {
        this.images = this.images.filter(i => i.image_id !== image.image_id);
      }
    });
  }
}
